import React from "react";
import { useTranslation } from "react-i18next";
import { formatMoney } from "../lib/api.js";
import { formatBillingDate } from "../lib/formatBillingDate.js";
import type { BillingInterval } from "../../../shared/types.js";

/**
 * What a new customer agrees to on the payment screens (card and PIX): today's charge, the
 * renewal amount and when it starts, that it renews until cancelled, and the 7-day guarantee.
 * The card screen used to show none of it, only Stripe's own summary inside its frame.
 */
export function RenewalTerms({
  todayCents,
  renewalCents,
  interval,
  nextChargeAt,
  installments,
}: {
  todayCents: number;
  /** What the provider charges every cycle after today's. */
  renewalCents: number;
  interval: BillingInterval;
  /** Next full charge; omitted for orders created before it was stored. */
  nextChargeAt?: string | null;
  /**
   * Today's charge split on the card (annual on Stone): shown as the count and the value of each. The
   * yearly amount is left out, since "R$ 420 por ano" read as one charge of the whole year.
   */
  installments?: { count: number; cents: number } | null;
}) {
  const { t, i18n } = useTranslation();
  const money = (cents: number) => formatMoney(cents, i18n.language);
  const annual = interval === "annual";
  const then = nextChargeAt
    ? t(annual ? "renewalTerms.thenAnnual" : "renewalTerms.thenMonthly", {
        amount: money(renewalCents),
        date: formatBillingDate(nextChargeAt, i18n.language),
      })
    : t(annual ? "renewalTerms.thenAnnualNoDate" : "renewalTerms.thenMonthlyNoDate", {
        amount: money(renewalCents),
      });

  return (
    <section
      className="mt-4 rounded-lg border border-purple-deep/25 bg-purple-deep/5 p-4 text-sm"
      aria-label={t("renewalTerms.title")}
    >
      <h2 className="font-medium text-purple-deep">{t("renewalTerms.title")}</h2>
      <p className="mt-2 font-medium">
        {installments
          ? t("renewalTerms.todayInstallments", {
              count: installments.count,
              value: money(installments.cents),
              amount: money(todayCents),
            })
          : t("renewalTerms.today", { amount: money(todayCents) })}
      </p>
      {!installments && <p className="mt-1">{then}</p>}
      <p className="mt-2 text-ink-2">{t("renewalTerms.autoRenew")}</p>
      <p className="mt-1 text-ink-2">{t("plans.guarantee")}</p>
    </section>
  );
}
